import {useCallback, useEffect, useRef, useState} from "react";
import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
    selectTenant,
    selectUserId,
    selectUserRole,
} from "../../../store/features/Auth/authSlice";
import {
    useConfirmEnsAlertMutation,
    useLazyListMyEnsAlertNotificationsQuery,
} from "../../../store/features/ens/ensService";
import {EnsAlertNotification} from "../../../store/features/ens/types";
import {useAppDispatch} from "../../../store/store";
import api from "../../../store/api";
import {browserNotificationService} from "../../../services/browserNotificationService";
import {EnsNotificationToast} from "./EnsNotificationToast";
import {
    ENS_NOTIFICATION_PAGE_LIMIT,
    ENS_NOTIFICATION_POLL_INTERVAL_MS,
    getEnsNotificationCursorKey,
    getEnsNotificationRetryDelay,
    readEnsNotificationCursor,
    takeUnseenEnsNotifications,
    writeEnsNotificationCursor,
} from "./ensNotificationPolling";
import { focusAppWindow } from '../../../utils/desktopBridge';

interface Props {
    enabled: boolean;
}

const compactMessage = (message: string): string => {
    const normalized = message.replace(/\s+/g, ' ').trim();
    return normalized.length > 180 ? `${normalized.slice(0, 179)}…` : normalized;
};

export const EnsNotificationPoller = ({enabled}: Props) => {
    const tenant = useSelector(selectTenant);
    const userId = useSelector(selectUserId);
    const role = useSelector(selectUserRole);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [loadNotifications] = useLazyListMyEnsAlertNotificationsQuery();
    const [confirmAlert, {isLoading: confirming}] = useConfirmEnsAlertMutation();
    const [toastQueue, setToastQueue] = useState<EnsAlertNotification[]>([]);
    const [confirmedNotificationId, setConfirmedNotificationId] = useState<number | null>(null);
    const [confirmationError, setConfirmationError] = useState<string | null>(null);
    const [desktopPermission, setDesktopPermission] = useState<
        NotificationPermission | 'unsupported'
    >(() => 'Notification' in window ? Notification.permission : 'unsupported');
    const deferredTimersRef = useRef<Set<number>>(new Set());

    const scopeKey = enabled && tenant && userId ? `${tenant}:${userId}` : null;
    const scopeKeyRef = useRef(scopeKey);
    scopeKeyRef.current = scopeKey;

    const openNotification = useCallback(() => {
        focusAppWindow();
        navigate('/ens/my-alerts');
    }, [navigate]);

    const enqueueToast = useCallback((notification: EnsAlertNotification) => {
        setToastQueue((current) =>
            current.some(({notificationId}) => notificationId === notification.notificationId)
                ? current
                : [...current, notification],
        );
    }, []);

    const presentNotification = useCallback((notification: EnsAlertNotification) => {
        const appIsActive = document.visibilityState === 'visible' && document.hasFocus();
        const currentPermission = 'Notification' in window
            ? Notification.permission
            : 'unsupported';
        setDesktopPermission(currentPermission);

        if (!appIsActive && currentPermission === 'granted') {
            try {
                const browserNotification = new Notification(notification.name, {
                    body: compactMessage(notification.message),
                    icon: '/favicon.png',
                    badge: '/favicon.png',
                    tag: `ens-notification-${notification.notificationId}`,
                    data: {
                        type: 'ens-alert',
                        publicId: notification.publicId,
                    },
                });
                browserNotification.onclick = () => {
                    enqueueToast(notification);
                    openNotification();
                    browserNotification.close();
                };
                return;
            } catch {
                // Fall back to the in-page acknowledgement panel.
            }
        }

        enqueueToast(notification);
    }, [enqueueToast, openNotification]);
    // React Router can replace navigate after a route change. Read the latest
    // presenter through a ref so navigation does not restart polling and clear
    // the dialog queue for the notification that was just clicked.
    const presentNotificationRef = useRef(presentNotification);
    presentNotificationRef.current = presentNotification;

    useEffect(() => {
        if (!scopeKey || !tenant || !userId) {
            setToastQueue([]);
            setConfirmedNotificationId(null);
            setConfirmationError(null);
            return;
        }

        const cursorKey = getEnsNotificationCursorKey(tenant, userId);
        let cursor = readEnsNotificationCursor(cursorKey);
        let failureCount = 0;
        let stopped = false;
        let polling = false;
        let pollTimer: number | null = null;
        let activeRequest: {abort: () => void; unsubscribe: () => void} | null = null;
        const seenNotificationIds = new Set<number>();

        setToastQueue([]);
        setConfirmedNotificationId(null);
        setConfirmationError(null);

        const clearPollTimer = () => {
            if (pollTimer != null) {
                window.clearTimeout(pollTimer);
                pollTimer = null;
            }
        };

        const schedulePoll = (delay: number) => {
            if (stopped) return;
            clearPollTimer();
            pollTimer = window.setTimeout(() => {
                void poll();
            }, delay);
        };

        const poll = async () => {
            if (stopped || polling) return;
            if (!navigator.onLine) {
                schedulePoll(ENS_NOTIFICATION_POLL_INTERVAL_MS);
                return;
            }

            polling = true;
            let nextDelay = ENS_NOTIFICATION_POLL_INTERVAL_MS;

            try {
                let hasMore = false;

                do {
                    const requestedCursor = cursor;
                    const establishingBaseline = requestedCursor == null;
                    const request = loadNotifications({
                        ...(requestedCursor != null ? {afterNotificationId: requestedCursor} : {}),
                        limit: ENS_NOTIFICATION_PAGE_LIMIT,
                    }, false);
                    activeRequest = request;

                    let response;
                    try {
                        response = await request.unwrap();
                    } finally {
                        request.unsubscribe();
                        if (activeRequest === request) activeRequest = null;
                    }

                    if (stopped) return;
                    if (!Number.isSafeInteger(response.nextAfterNotificationId) ||
                        response.nextAfterNotificationId < 0) {
                        throw new Error('ENS notification poll returned an invalid cursor');
                    }

                    if (!establishingBaseline) {
                        const notifications = takeUnseenEnsNotifications(
                            response.data,
                            seenNotificationIds,
                        );
                        notifications.forEach((notification) => {
                            presentNotificationRef.current(notification);
                        });
                        if (notifications.length > 0) {
                            dispatch(api.util.invalidateTags([{type: 'EnsMyAlerts', id: 'LIST'}]));
                        }
                    }

                    cursor = response.nextAfterNotificationId;
                    writeEnsNotificationCursor(cursorKey, cursor);
                    hasMore = response.hasMore;

                    if (hasMore && cursor === requestedCursor) {
                        throw new Error('ENS notification poll cursor did not advance');
                    }
                } while (hasMore && !stopped);

                failureCount = 0;
                nextDelay = ENS_NOTIFICATION_POLL_INTERVAL_MS;
            } catch {
                if (stopped) return;
                failureCount += 1;
                nextDelay = getEnsNotificationRetryDelay(failureCount);
            } finally {
                polling = false;
                if (!stopped) schedulePoll(nextDelay);
            }
        };

        const pollImmediately = () => {
            if (stopped || polling) return;
            clearPollTimer();
            void poll();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') pollImmediately();
        };

        window.addEventListener('online', pollImmediately);
        window.addEventListener('pageshow', pollImmediately);
        window.addEventListener('focus', pollImmediately);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        void poll();

        return () => {
            stopped = true;
            clearPollTimer();
            activeRequest?.abort();
            activeRequest?.unsubscribe();
            window.removeEventListener('online', pollImmediately);
            window.removeEventListener('pageshow', pollImmediately);
            window.removeEventListener('focus', pollImmediately);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        dispatch,
        loadNotifications,
        scopeKey,
        tenant,
        userId,
    ]);

    useEffect(() => {
        deferredTimersRef.current.forEach((timer) => window.clearTimeout(timer));
        deferredTimersRef.current.clear();
    }, [scopeKey]);

    useEffect(() => () => {
        deferredTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    }, []);

    const activeNotification = toastQueue[0] ?? null;

    const dismissActive = useCallback(() => {
        setToastQueue((current) => current.slice(1));
        setConfirmedNotificationId(null);
        setConfirmationError(null);
    }, []);

    const openActive = useCallback(() => {
        if (!activeNotification) return;
        openNotification();
        dismissActive();
    }, [activeNotification, dismissActive, openNotification]);

    const confirmActive = useCallback(async () => {
        if (!activeNotification || confirming) return;
        const notification = activeNotification;
        const scheduledScope = scopeKey;
        setConfirmationError(null);

        try {
            await confirmAlert(notification.publicId).unwrap();
            setConfirmedNotificationId(notification.notificationId);

            const timer = window.setTimeout(() => {
                deferredTimersRef.current.delete(timer);
                if (scopeKeyRef.current !== scheduledScope) return;
                setToastQueue((current) =>
                    current.filter(({publicId}) => publicId !== notification.publicId),
                );
                setConfirmedNotificationId(null);
            }, 1_800);
            deferredTimersRef.current.add(timer);
        } catch {
            setConfirmationError('The alert could not be confirmed. Open the alert and try again.');
        }
    }, [activeNotification, confirmAlert, confirming, scopeKey]);

    const enableDesktopNotifications = useCallback(async () => {
        if (!('Notification' in window)) {
            setDesktopPermission('unsupported');
            return;
        }
        await browserNotificationService.requestPermission();
        setDesktopPermission(Notification.permission);
    }, []);

    if (!activeNotification) return null;

    return (
        <Dialog.Root open onOpenChange={() => undefined}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[999] bg-scrim backdrop-blur-sm"/>
                <Dialog.Content
                    onPointerDownOutside={(event) => event.preventDefault()}
                    onInteractOutside={(event) => event.preventDefault()}
                    onEscapeKeyDown={(event) => event.preventDefault()}
                    className="fixed left-1/2 top-1/2 z-[1000] w-[min(540px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 focus:outline-none"
                >
                    <Dialog.Title className="sr-only">{activeNotification.name}</Dialog.Title>
                    <Dialog.Description className="sr-only">
                        Emergency alert requiring review or confirmation.
                    </Dialog.Description>
                    <div aria-live="assertive">
                        <EnsNotificationToast
                            key={activeNotification.notificationId}
                            notification={activeNotification}
                            canConfirm={role === 'USER' || role === 'ADMIN' || role === 'SUPERADMIN'}
                            confirmed={confirmedNotificationId === activeNotification.notificationId}
                            confirming={confirming}
                            error={confirmationError}
                            desktopPermission={desktopPermission}
                            onConfirm={() => void confirmActive()}
                            onEnableDesktopNotifications={() => void enableDesktopNotifications()}
                            onOpen={openActive}
                            onDismiss={dismissActive}
                        />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
